import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismIcon,
  PrismMetricBlock,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDemandLevel from 'lumen/components/general/demand-level';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import highlightElement from 'lumen/modifiers/highlight-element';
import { fetchEventsByVisibilityFlag } from 'lumen/utils/market-insight/market-insight-helpers';

interface HotelMarketInsightCardsDemandIndicatorsSignature {
  Args: {
    alternativeLodgingSummary: unknown;
    alternativeLodgingSummaryTooltipText: unknown;
    detailSectionId: unknown;
    isLoading: unknown;
    refDaysAgo: unknown;
    selectedDayModel: unknown;
    setFocusedDetailSection: unknown;
    summaryForCurrentSelection: unknown;
  };
}

export default class HotelMarketInsightCardsDemandIndicators extends Component<HotelMarketInsightCardsDemandIndicatorsSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare miAlternativeLodging: Services['mi-alternative-lodging'];
  @service declare miSelections: Services['mi-selections'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @computed('args.selectedDayModel.calendarEvents')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get acceptedEvents() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.calendarEvents;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventsLabel() {
    if (this.miSelections.showAllEvents) {
      return this.intl.t('events.events');
    }

    return this.intl.t('events.accepted_events');
  }

  @computed('args.selectedDayModel.holidays')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get holidays() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.holidays;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfEventsAndHolidays() {
    let numberOfEvents = 0;
    let numberOfHolidays = 0;

    if (this.args.selectedDayModel) {
      numberOfEvents = fetchEventsByVisibilityFlag(
        // @ts-expect-error: Incorrect type
        this.args.selectedDayModel,
        this.miSelections.showAllEvents,
      ).length;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      numberOfHolidays = this.holidays.length;
    }

    return {
      events: numberOfEvents,
      holidays: numberOfHolidays,
      areEventsActionable: numberOfEvents > 0,
      areHolidaysActionable: numberOfHolidays > 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEventsAndHolidaysRow() {
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.acceptedEvents.length ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (this.miSelections.showAllEvents && this.unacceptedEvents.length) ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.holidays.length
    ) {
      return true;
    }

    return false;
  }

  @computed('args.selectedDayModel.unacceptedEvents')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unacceptedEvents() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.unacceptedEvents;
  }

  @action
  viewDemandIndicatorsButtonClicked(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const day = this.args.selectedDayModel.date;
    // @ts-expect-error: Incorrect type
    const { outliers } = this.args.selectedDayModel;

    this.usageAnalytics.track('Demand Indicators Viewed', {
      eventTrigger: 'day-detail-see-more',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      daysOffsetFromToday: day
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .startOf('day')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .diff(this.hotelAndMarketService.getLocalTime().startOf('day'), 'days'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      isOutlier: outliers.length > 0,
    });

    // @ts-expect-error: Incorrect type
    this.args.setFocusedDetailSection(this.args.detailSectionId);
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      class="detail-pane-card"
      {{highlightElement id=@detailSectionId isLoading=@isLoading}}
    >
      <:header as |Header|>
        <Header.title>
          {{t "market_insight.demand_indicators"}}
        </Header.title>
      </:header>
      <:headerAfter>
        <button
          class="back-button button--unstyled flex flex-row items-center"
          data-test-id="detail-pane-demand-view-button"
          type="button"
          {{on "click" this.viewDemandIndicatorsButtonClicked}}
        >
          <p class="text-200--regular mr-100">{{t "general.see_details"}}</p>
          <PrismIcon @name="arrow-right" @size="small" />
        </button>
      </:headerAfter>
      <:content>
        <div class="mb-400 flex flex-row">
          <PrismMetricBlock
            @align="left"
            @compact={{true}}
            class="mr-800 w-1/3"
          >
            <:value>
              <span class="text-400--bold">
                <GeneralDemandLevel
                  @demandLevel={{@summaryForCurrentSelection.demandLevelColorScaleNumber}}
                  @size="normal"
                />
              </span>
            </:value>
            <:label>
              <span class="flex items-center gap-100">
                <span class="truncate">
                  {{t "demand.demand_forecast"}}
                  <PrismTooltip
                    @side="right"
                    @text={{t "demand.demand_forecast"}}
                  />
                </span>

                <GeneralIconWithTooltip
                  @side="right"
                  @tooltipContent={{t
                    "market_insight.kpi_explanation.submarket_demand"
                  }}
                  @tooltipTitle={{t "demand.demand_forecast"}}
                />
              </span>
            </:label>
          </PrismMetricBlock>

          {{#if @summaryForCurrentSelection.isLatest}}
            <PrismMetricBlock
              @align="left"
              @compact={{true}}
              class="detail-pane-metric-block"
            >
              <:value>
                <span
                  class="text-400--bold
                    {{unless
                      @summaryForCurrentSelection.hasDemandChange
                      'non-actionable-value'
                    }}"
                >
                  <div class="flex flex-row items-center gap-200">
                    {{#if @summaryForCurrentSelection.hasDemandChange}}
                      <GeneralDemandLevel
                        @demandLevel={{@summaryForCurrentSelection.demandLevelColorScaleNumberRefPeriod}}
                        @size="normal"
                      />
                      <PrismIcon @name="chevron-right" @size="small" />
                      <GeneralDemandLevel
                        @demandLevel={{@summaryForCurrentSelection.demandLevelColorScaleNumber}}
                        @size="normal"
                      />
                    {{else}}
                      {{t "overview_dashboard.kpi.no_change"}}
                    {{/if}}
                  </div>
                </span>
              </:value>
              <:label>
                <span
                  class={{unless
                    @summaryForCurrentSelection.hasDemandChange
                    "non-actionable-value"
                  }}
                  data-test-id="detail-pane-demand-label-daysago"
                >
                  {{t "market_insight.submarket_demand"}}
                  {{t "general.daysago" count=@refDaysAgo}}
                  <PrismTooltip
                    @side="right"
                    @text={{concat
                      (t "market_insight.submarket_demand")
                      " "
                      (t "general.daysago" count=7)
                    }}
                  />
                </span>
              </:label>
            </PrismMetricBlock>
          {{/if}}
        </div>

        <div class="divider-line"></div>

        {{#if this.showEventsAndHolidaysRow}}
          <div class="flex flex-row">
            <PrismMetricBlock @align="left" class="mr-800 w-1/3">
              <:value>
                <span
                  class="text-400--bold
                    {{unless
                      this.numberOfEventsAndHolidays.areEventsActionable
                      'non-actionable-value'
                    }}"
                >{{this.numberOfEventsAndHolidays.events}}</span>
              </:value>
              <:label>
                <span
                  class={{unless
                    this.numberOfEventsAndHolidays.areEventsActionable
                    "non-actionable-value"
                  }}
                  data-test-id="detail-pane-demand-indicator-events"
                >
                  {{this.eventsLabel}}
                  <PrismTooltip @side="right" @text={{this.eventsLabel}} />
                </span>
              </:label>
            </PrismMetricBlock>
            <PrismMetricBlock @align="left" class="detail-pane-metric-block">
              <:value>
                <span
                  class="text-400--bold
                    {{unless
                      this.numberOfEventsAndHolidays.areHolidaysActionable
                      'non-actionable-value'
                    }}"
                >{{this.numberOfEventsAndHolidays.holidays}}</span>
              </:value>
              <:label>
                <span
                  class={{unless
                    this.numberOfEventsAndHolidays.areHolidaysActionable
                    "non-actionable-value"
                  }}
                >
                  {{t "events.holidays"}}
                  <PrismTooltip @side="right" @text={{t "events.holidays"}} />
                </span>
              </:label>
            </PrismMetricBlock>
          </div>

          <div class="divider-line"></div>
        {{/if}}

        <div class="mt-300 flex flex-row">
          <PrismMetricBlock
            @align="left"
            @compact={{true}}
            class="mr-800 w-1/3"
          >
            <:value>
              <span
                class="text-400--bold"
              >{{@summaryForCurrentSelection.unavailableHotelsPercentageNumber}}%</span>
            </:value>
            <:label>
              <span class="flex items-center gap-100">
                <span class="truncate">
                  {{t "market_insight.availability.unavailable_hotels"}}
                  <PrismTooltip
                    @side="right"
                    @text={{t "market_insight.availability.unavailable_hotels"}}
                  />
                </span>

                <GeneralIconWithTooltip
                  @side="right"
                  @tooltipContent={{t
                    "market_insight.kpi_explanation.unavailable_hotels"
                  }}
                  @tooltipTitle={{t
                    "market_insight.availability.unavailable_hotels"
                  }}
                />
              </span>
            </:label>
          </PrismMetricBlock>
          {{#if this.miAlternativeLodging.isAltLodgingAirBnbSupported}}
            <PrismMetricBlock
              @align="left"
              @compact={{true}}
              class="detail-pane-metric-block"
            >
              <:value>
                <span
                  class="text-400--bold"
                >{{@alternativeLodgingSummary.formattedUnavailableHomesPercentageNumberMetricValue}}</span>
              </:value>
              <:label>
                <span class="flex items-center gap-100"><span class="truncate">
                    {{t
                      "reports.market_insight.column.unavailable_alt_lodging"
                    }}
                    <PrismTooltip
                      @side="right"
                      @text={{t
                        "reports.market_insight.column.unavailable_alt_lodging"
                      }}
                    />
                  </span>

                  <GeneralIconWithTooltip
                    @side="right"
                    @tooltipContent={{@alternativeLodgingSummaryTooltipText}}
                    @tooltipTitle={{t
                      "market_insight.availability.alt_lodgings.unavailable_alt_lodging"
                    }}
                  />
                </span>
              </:label>

            </PrismMetricBlock>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
